import { useEffect } from 'react';

/**
 * TikTok Pixel 追蹤組件
 *
 * 使用方式:
 * 1. 在 App.js 中引入: <TikTokPixel pixelId="YOUR_PIXEL_ID" />
 * 2. 在其他組件中使用 window.ttq 追蹤事件
 *
 * 事件追蹤範例:
 * - 頁面瀏覽: window.ttq.page()
 * - 查看商品: window.ttq.track('ViewContent', { content_id: '123', content_type: 'product' })
 * - 加入購物車: window.ttq.track('AddToCart', { content_id: '123', value: 100, currency: 'TWD' })
 * - 開始結帳: window.ttq.track('InitiateCheckout', { value: 500, currency: 'TWD' })
 * - 完成付款: window.ttq.track('CompletePayment', { value: 500, currency: 'TWD' })
 */
const TikTokPixel = ({ pixelId }) => {
  useEffect(() => {
    if (!pixelId) {
      console.log('TikTok Pixel: 未設定 Pixel ID，跳過初始化');
      return;
    }

    // 檢查是否已經初始化
    if (window.ttq) {
      console.log('TikTok Pixel: 已初始化');
      return;
    }

    // TikTok Pixel Base Code
    // eslint-disable-next-line no-unused-expressions
    (function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = w[t] = w[t] || [];
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
      ttq.setAndDefer = function (t, e) {
        t[e] = function () {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      for (var i = 0; i < ttq.methods.length; i++) {
        ttq.setAndDefer(ttq, ttq.methods[i]);
      }
      ttq.instance = function (t) {
        var e = ttq._i[t] || [];
        for (var n = 0; n < ttq.methods.length; n++) {
          ttq.setAndDefer(e, ttq.methods[n]);
        }
        return e;
      };
      ttq.load = function (e, n) {
        var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {};
        ttq._i[e] = [];
        ttq._i[e]._u = i;
        ttq._t = ttq._t || {};
        ttq._t[e] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[e] = n || {};
        var o = document.createElement("script");
        o.type = "text/javascript";
        o.async = true;
        o.src = i + "?sdkid=" + e + "&lib=" + t;
        var a = document.getElementsByTagName("script")[0];
        a.parentNode.insertBefore(o, a);
      };

      // 載入 Pixel
      ttq.load(pixelId);
      ttq.page();

      console.log('TikTok Pixel: 初始化完成, ID:', pixelId);
    })(window, document, 'ttq');

    // Cleanup (optional)
    return () => {
      // TikTok Pixel 不需要清理
    };
  }, [pixelId]);

  return null; // 這是一個無 UI 的組件
};

/**
 * TikTok Pixel 事件追蹤工具函數
 */
export const trackTikTokEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.ttq) {
    try {
      window.ttq.track(eventName, params);
      console.log('TikTok Pixel Event:', eventName, params);
    } catch (error) {
      console.error('TikTok Pixel Event Error:', error);
    }
  }
};

/**
 * 追蹤查看商品
 */
export const trackTikTokViewContent = (product) => {
  trackTikTokEvent('ViewContent', {
    content_id: String(product.id || product.sku),
    content_type: 'product',
    content_name: product.name,
    price: parseFloat(product.sale_price || product.price),
    currency: 'TWD'
  });
};

/**
 * 追蹤加入購物車
 */
export const trackTikTokAddToCart = (product, quantity = 1) => {
  trackTikTokEvent('AddToCart', {
    content_id: String(product.id || product.sku),
    content_type: 'product',
    content_name: product.name,
    quantity: quantity,
    value: parseFloat(product.sale_price || product.price) * quantity,
    currency: 'TWD'
  });
};

/**
 * 追蹤開始結帳
 */
export const trackTikTokInitiateCheckout = (cart) => {
  const value = cart.items?.reduce((sum, item) =>
    sum + (parseFloat(item.sale_price) * item.quantity), 0) || cart.total || 0;

  trackTikTokEvent('InitiateCheckout', {
    content_type: 'product',
    content_ids: cart.items?.map(item => String(item.product_id)) || [],
    value: value,
    currency: 'TWD'
  });
};

/**
 * 追蹤完成付款
 */
export const trackTikTokCompletePayment = (order) => {
  trackTikTokEvent('CompletePayment', {
    content_type: 'product',
    content_ids: order.items?.map(item => String(item.product_id)) || [],
    value: parseFloat(order.total_amount || order.total),
    currency: 'TWD'
  });
};

/**
 * 追蹤會員註冊
 */
export const trackTikTokCompleteRegistration = (method = 'email') => {
  trackTikTokEvent('CompleteRegistration', {
    method: method
  });
};

/**
 * 追蹤搜尋
 */
export const trackTikTokSearch = (query) => {
  trackTikTokEvent('Search', {
    query: query
  });
};

export default TikTokPixel;
